import {
	Component, type ReactNode, createContext, useContext, useEffect, useRef, useState,
} from 'react';
import { Lottie } from 'lottie-react';
import {
	MessagePrimitive, ActionBarPrimitive, useAuiState, useMessagePartText,
	TextMessagePartProvider,
} from '@assistant-ui/react';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@uconnect/assets/lib/utils';
import { toast } from '@uconnect/assets/components/ui/sonner';
import Icon from '@uconnect/assets/components/ui/icon';
import { Button } from '@uconnect/assets/components/ui/button';
import Message from './Message';
import type { Source } from '../types';
import useMarkdownAwareSmooth from '../hooks/useMarkdownAwareSmooth';
import aiSearchingIcon from '../../images/ai-searching-lottie-animation.json';

type FeedbackType = 'positive' | 'negative' | null;

export interface FeedbackConfig {
	enabled?: boolean;
	positiveMessage?: string;
	negativeMessage?: string;
}

interface AssistantMessageProps {
	feedback?: FeedbackConfig;
	messageClassNames?: string | null;
	ucRef?: string;
	threadId?: string;
}

const REMARK_PLUGINS = [remarkGfm];

// Canonical Lightcast attribution URL, shown in the Sources box whenever an answer
// links to the site's LMI page (LMI data is provided by Lightcast).
const LIGHTCAST_ATTRIBUTION_URL = 'https://lightcast.io';

/**
 * Returns true when the message text contains a markdown link that resolves to the
 * site's LMI page. Compares origin + normalized pathname so it matches the LMI
 * permalink regardless of query args (occupation/geo) and is robust to custom slugs,
 * while not false-matching an external URL that happens to share the same path.
 * Returns false when no LMI page URL is configured.
 */
function responseLinksToLmiPage( text: string, lmiPageUrl?: string | null ): boolean {
	if ( ! text || ! lmiPageUrl ) {
		return false;
	}
	let targetOrigin: string;
	let targetPath: string;
	try {
		const targetUrl = new URL( lmiPageUrl, window.location.origin );
		targetOrigin = targetUrl.origin;
		targetPath = targetUrl.pathname.replace( /\/+$/, '' ).toLowerCase();
	} catch {
		return false;
	}
	const linkRegex = /\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
	let match;
	while ( ( match = linkRegex.exec( text ) ) !== null ) {
		try {
			const linkUrl = new URL( match[ 1 ], window.location.origin );
			const path = linkUrl.pathname.replace( /\/+$/, '' ).toLowerCase();
			if ( linkUrl.origin === targetOrigin && path === targetPath ) {
				return true;
			}
		} catch {
			// Skip hrefs that aren't valid URLs.
		}
	}
	return false;
}

const UcRefContext = createContext< string | undefined >( undefined );
const ThreadIdContext = createContext< string | undefined >( undefined );
// True when this message was streamed live (loading indicator showed before text arrived).
// Causes useMarkdownAwareSmooth to animate even when all tokens arrive in a single batched render.
const WasStreamedContext = createContext< boolean >( false );

/**
 * Appends a `uc_ref` query parameter to a URL for internal referral tracking.
 * If `ref` is not provided, the URL is returned unchanged.
 */
function appendUcRef( url: string, ref?: string ): string {
	const ucRefParam = window.agentConfig?.ucRefParam;
	if ( ! ref || ! ucRefParam ) {
		return url;
	}
	try {
		const u = new URL( url, window.location.origin );
		u.searchParams.set( ucRefParam, ref );
		return u.toString();
	} catch {
		return url;
	}
}

/**
 * Replaces inline [>post_id<] markers with markdown superscript links.
 */
function processCitations( text: string, sources?: Source[], ucRef?: string ): string {
	if ( ! sources?.length ) {
		// Strip any leftover citation markers when no sources are available.
		return text.replace( /\[>[^<]*<\]/g, '' );
	}

	const postIdToIndex = new Map< number, number >();
	sources.forEach( ( source, i ) => {
		postIdToIndex.set( source.post_id, i + 1 );
	} );

	return text.replace( /\[>[^<]*<\]/g, ( match ) => {
		const rawId = match.slice( 2, -2 );
		if ( ! /^\d+$/.test( rawId ) ) {
			return '';
		}
		const postId = Number( rawId );
		const index = postIdToIndex.get( postId );
		if ( ! index ) {
			return '';
		}
		const source = sources[ index - 1 ];
		return `[${index}](${appendUcRef( source.url, ucRef )})`;
	} );
}


/**
 * Custom text component that processes inline [>post_id<] citations
 * before rendering as markdown.
 */
function CitationText() {
	const ucRef = useContext( UcRefContext );
	const threadId = useContext( ThreadIdContext );
	const { text } = useMessagePartText();
	const metadata = useAuiState( ( s ) => ( s.message as any )?.metadata );
	const sources = metadata?.custom?.sources as Source[] | undefined;
	const isStreaming = Boolean( metadata?.custom?.isStreaming );
	const wasStreamed = useContext( WasStreamedContext );

	const processedText = processCitations( text, sources, ucRef );
	const smoothText = useMarkdownAwareSmooth( processedText, isStreaming || wasStreamed );

	return (
		<TextMessagePartProvider text={ smoothText } isRunning={ isStreaming }>
			<MarkdownTextPrimitive
				smooth={ false }
				className="markdown-content"
				remarkPlugins={ REMARK_PLUGINS }
				components={ {
					table: ( { children, node, ...props } ) => (
						<div className="table-wrapper">
							<table { ...props }>{ children }</table>
						</div>
					),
					a: ( { children, ...props } ) => {
						const num = String( children );
						if ( /^\d+$/.test( num ) ) {
							const source = sources?.[ Number( num ) - 1 ];
							return (
								<a
									{ ...props }
									target="_blank"
									rel="noopener noreferrer"
									className={ cn(
										"inline-flex! items-center justify-center py-0.5! px-1! mx-1! rounded",
										"text-[0.6em] align-super! font-semibold leading-none! not-italic!",
										"cursor-pointer no-underline hover:no-underline!",
										"bg-citation-background! text-citation-foreground!",
										"hover:bg-citation-foreground! hover:text-citation-background!"
									) }
									aria-label={ source ? `Source ${ num }, ${ source.title }` : `Source ${ num }` }
									onClick={ () => {
										window.uconnect?.analytics?.send_event?.( 'citation_click', {
											post_id: source?.post_id,
											post_title: source?.title?.slice( 0, 100 ),
											post_url: source?.url?.slice( 0, 100 ),
											citation_index: Number( num ),
											thread_id: threadId,
											source_type: 'inline',
										} );
									} }
								>
									{ children }
								</a>
							);
						}
						return <a { ...props } href={ appendUcRef( props.href ?? '', ucRef ) } target="_blank" rel="noopener noreferrer">{ children }</a>;
					},
				} }
			/>
		</TextMessagePartProvider>
	);
}

function extractMessageText( content: unknown ): string {
	if ( typeof content === 'string' ) {
		return content;
	}
	if ( Array.isArray( content ) ) {
		return content
			.map( ( part ) => {
				if ( typeof part === 'string' ) {
					return part;
				}
				if ( part && typeof part === 'object' && 'text' in part ) {
					const text = ( part as { text?: unknown } ).text;
					return typeof text === 'string' ? text : '';
				}
				return '';
			} )
			.join( '' );
	}
	return '';
}

/**
 * Displays a numbered list of source links below the message.
 * Only includes sources that are actually cited in the message text.
 */
function SourcesFooter() {
	const ucRef = useContext( UcRefContext );
	const threadId = useContext( ThreadIdContext );
	const isStreaming = useAuiState(
		( s ) => Boolean( ( s.message as any )?.metadata?.custom?.isStreaming )
	);
	const sources = useAuiState(
		( s ) => ( s.message as any )?.metadata?.custom?.sources as Source[] | undefined
	);
	const messageText = useAuiState(
		( s ) => extractMessageText( ( s.message as any )?.content ?? '' )
	);
	const [ visible, setVisible ] = useState( false );
	useEffect( () => { setVisible( true ); }, [] );

	if ( isStreaming ) {
		return null;
	}

	// Computed only after streaming completes, so it runs against the full final answer.
	const hasLmiAttribution = responseLinksToLmiPage( messageText, window.agentConfig?.lmiPageUrl );

	if ( ! sources?.length && ! hasLmiAttribution ) {
		return null;
	}

	return (
		<div className="mt-3! p-3! text-sm bg-muted! rounded">
			<div className="font-semibold mb-1!">Sources</div>
			{ !! sources?.length && (
				<ol className={ cn(
					'mb-0! flex flex-col gap-1',
					sources.length >= 10 ? 'ml-[3ch]!' : 'ml-[2ch]!'
				) }
			>
					{ sources.map( ( source, index ) => (
						<li key={ `${ source.post_id }-${ index }` }>
							<a
								href={ appendUcRef( source.url, ucRef ) }
								target="_blank"
								rel="noopener noreferrer"
								title={ source.snippet }
								className="underline underline-offset-2 break-words"
								onClick={ () => {
									window.uconnect?.analytics?.send_event?.( 'citation_click', {
										post_id: source.post_id,
										post_title: source.title.slice( 0, 100 ),
										post_url: source.url.slice( 0, 100 ),
										citation_index: index + 1,
										thread_id: threadId,
										source_type: 'footer',
									} );
								} }
							>
								{ source.title }
							</a>
						</li>
					) ) }
				</ol>
			) }

			{ hasLmiAttribution && (
				<div className={ cn(
					'text-muted-foreground! pt-[10px]!',
					sources?.length ? 'mt-2!' : ''
				) }
				>
					Labor Market Insights data powered by{ ' ' }
					<a
						href={ LIGHTCAST_ATTRIBUTION_URL }
						target="_blank"
						rel="noopener noreferrer"
						className="underline! underline-offset-2"
					>
						Lightcast
					</a>
				</div>
			) }
		</div>
	);
}

/**
 * Catches the transient `useClientLookup: Index out of bounds` error thrown by
 * assistant-ui's PartByIndexProvider when a thread switch clears message parts
 * while CitationText is still mounted. Renders nothing on error; the component
 * unmounts naturally on the next reconciliation cycle.
 */
class MessageContentBoundary extends Component<
	{ children: ReactNode },
	{ error: unknown | null }
> {
	constructor( props: { children: ReactNode } ) {
		super( props );
		this.state = { error: null };
	}
	static getDerivedStateFromError( error: unknown ) {
		return { error };
	}
	render() {
		const { children } = this.props;
		const { error } = this.state;
		if ( ! error ) {
			return children;
		}

		const message = error instanceof Error ? error.message : String( error );
		if ( message.includes( 'useClientLookup: Index out of bounds' ) ) {
			return null;
		}

		throw error;
	}
}

export default function AssistantMessage( { feedback = {}, messageClassNames = null, ucRef, threadId }: AssistantMessageProps ) {
	const {
		enabled: feedbackEnabled = false,
		positiveMessage = 'Thanks for the feedback',
		negativeMessage = 'Thanks for the feedback',
	} = feedback;
	const isRunning = useAuiState( ( s ) => ( s.message as any )?.status?.type === 'running' );
	const wasStopped = useAuiState( ( s ) => Boolean( ( s.message as any )?.metadata?.custom?.wasStopped ) );
	const hasContent = useAuiState( ( s ) => {
		const text = extractMessageText( ( s.message as any )?.content ?? '' ).trim();
		return text.length > 0 && text !== '…' && text !== '...';
	} );
	const statusText = useAuiState( ( s ) => ( s.message as any )?.metadata?.custom?.statusText as string | undefined );
	const showLoadingIndicator = isRunning && ! hasContent;

	// Track whether the loading indicator was ever visible for this message.
	// Set synchronously during render so it's true in the same render cycle where
	// loading hides and real content appears (handles React 18 batched token delivery).
	const wasLoadingRef = useRef( false );
	if ( showLoadingIndicator ) wasLoadingRef.current = true;
	const wasStreamed = wasLoadingRef.current;

	// Initialize feedback state from hydrated thread history.
	const existingFeedback = useAuiState(
		( s ) => ( s.message as any )?.metadata?.custom?.feedback as string | undefined
	);
	const [ localFeedback, setLocalFeedback ] = useState<FeedbackType>( () => {
		if ( existingFeedback === 'upvote' ) return 'positive';
		if ( existingFeedback === 'downvote' ) return 'negative';
		return null;
	} );

	useEffect( () => {
		if ( existingFeedback === 'upvote' ) setLocalFeedback( 'positive' );
		else if ( existingFeedback === 'downvote' ) setLocalFeedback( 'negative' );
	}, [ existingFeedback ] );

	// Don't return the message component at all if there is an error, etc. to prevent spacing issues
	if ( ! isRunning && ! hasContent && ! wasStopped ) {
		return null;
	}

	const handlePositiveFeedback = () => {
		setLocalFeedback( 'positive' );
		toast.success( positiveMessage );
	};

	const handleNegativeFeedback = () => {
		setLocalFeedback( 'negative' );
		toast.success( negativeMessage );
	};

	return (
		<ThreadIdContext.Provider value={ threadId }>
		<UcRefContext.Provider value={ ucRef }>
		<MessagePrimitive.Root className={ cn(
			'agent-assistant-message mb-4 relative group',
			showLoadingIndicator ? 'flex items-start' : 'flex flex-col'
		) }
		>
			{ showLoadingIndicator ? (
				<div
					className={ cn(
						"agent-assistant-message-content bg-agent-chat-background! text-agent-chat-foreground!",
						"text-base font-medium text-secondary! animate-pulse flex! items-center gap-2",
					) }
					role="status"
					aria-live="polite"
				>
					{ /* lottie-react v3 draws into this element itself and defaults it to width/height 100%
					     from its own @layer lottie-react. React injects that layer after our Tailwind layers, so it
					     sorts last and plain utilities lose to it -- `!` wins regardless of layer order. The
					     animation is square, so the width is given explicitly rather than left auto. */ }
					<Lottie
						src={ aiSearchingIcon }
						loop
						autoplay
						aria-hidden="true"
						className="h-6! w-6! shrink-0 [&_svg_path]:fill-current! [&_svg]:stroke-0!"
					/>
					<span className="">{ statusText || 'Searching…' }</span>
				</div>
			) : (
				<MessageContentBoundary>
					<WasStreamedContext.Provider value={ wasStreamed }>
						<Message
							className={ cn(
								'agent-assistant-message-content bg-agent-chat-background! text-agent-chat-foreground!',
								messageClassNames
							) }
						>
							<MessagePrimitive.Parts components={ { Text: CitationText } } />
							<SourcesFooter />
						</Message>
					</WasStreamedContext.Provider>
				</MessageContentBoundary>
			) }

			{ wasStopped && ! showLoadingIndicator && (
				<p className="text-sm! italic text-muted-foreground! my-3!">
					You stopped this search
				</p>
			) }

			{ feedbackEnabled && ! showLoadingIndicator && hasContent && (
				<ActionBarPrimitive.Root
					autohide="not-last"
					autohideFloat="always"
					className={ cn(
						'flex items-center gap-2 mt-2!',
						'opacity-100! visible!',
						'data-[floating=true]:absolute data-[floating=true]:-bottom-6 data-[floating=true]:-left-1',
						'data-[floating=true]:bg-white! data-[floating=true]:rounded-full',
						'data-[floating=true]:border! data-[floating=true]:border-border',
						'data-[floating=true]:px-2! data-[floating=true]:py-1!',
					) }
				>
					<ActionBarPrimitive.FeedbackPositive asChild>
						<Button
							variant="mutedInterface"
							onClick={ handlePositiveFeedback }
							aria-label="Thumbs up"
							className="leading-none! block! h-auto!"
						>
							<Icon
								name={ localFeedback === 'positive' ? 'thumbsUpSolid' : 'thumbsUp' }
								className="p-1! block! size-6"
							/>
						</Button>
					</ActionBarPrimitive.FeedbackPositive>

					<ActionBarPrimitive.FeedbackNegative asChild>
						<Button
							variant="mutedInterface"
							onClick={ handleNegativeFeedback }
							aria-label="Thumbs down"
							className="leading-none! block! h-auto!"
						>
							<Icon
								name={ localFeedback === 'negative' ? 'thumbsDownSolid' : 'thumbsDown' }
								className="p-1! block! size-6"
							/>
						</Button>
					</ActionBarPrimitive.FeedbackNegative>
				</ActionBarPrimitive.Root>
			) }
		</MessagePrimitive.Root>
		</UcRefContext.Provider>
		</ThreadIdContext.Provider>
	);
}
